import type { FeatureFlagAuditEntry, FeatureFlagsMap } from "@evolua/api-client";

export const DEMO_FLAG_KEYS = {
  employeeNotesEnabled: "employee.notes.enabled",
  vacationRequestEnabled: "vacation.request.enabled",
} as const;

const STORAGE_KEY = "evolua.demo.feature-flags";

type DemoFlagState = {
  /** tenantId → key → enabled */
  byTenant: Record<string, Record<string, boolean>>;
  audit: FeatureFlagAuditEntry[];
};

const DEFAULTS: Record<string, boolean> = {
  [DEMO_FLAG_KEYS.employeeNotesEnabled]: true,
  [DEMO_FLAG_KEYS.vacationRequestEnabled]: true,
};

function load(): DemoFlagState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { byTenant: {}, audit: [] };
    return JSON.parse(raw) as DemoFlagState;
  } catch {
    return { byTenant: {}, audit: [] };
  }
}

function save(state: DemoFlagState): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function resolveDemoFlags(tenantId: string): FeatureFlagsMap {
  const state = load();
  const overrides = state.byTenant[tenantId] ?? {};
  const flags: Record<string, boolean> = {};
  for (const key of Object.keys(DEFAULTS)) {
    flags[key] = overrides[key] ?? DEFAULTS[key]!;
  }
  return { flags };
}

export function setDemoFlag(input: {
  tenantId: string;
  key: string;
  enabled: boolean;
  actor: string;
}): FeatureFlagAuditEntry {
  const state = load();
  const prevMap = state.byTenant[input.tenantId] ?? {};
  const previous =
    input.key in prevMap ? prevMap[input.key]! : (DEFAULTS[input.key] ?? null);
  state.byTenant[input.tenantId] = { ...prevMap, [input.key]: input.enabled };
  const entry: FeatureFlagAuditEntry = {
    id: crypto.randomUUID(),
    flagKey: input.key,
    scope: "tenant",
    targetId: input.tenantId,
    previousEnabled: previous,
    nextEnabled: input.enabled,
    actor: input.actor,
    at: new Date().toISOString(),
  };
  state.audit = [...state.audit, entry];
  save(state);
  return entry;
}

export function listDemoFlagAudit(key?: string): FeatureFlagAuditEntry[] {
  const audit = load().audit;
  if (!key) return [...audit].reverse();
  return audit.filter((e) => e.flagKey === key).reverse();
}
